import {
	applySpec,
	equals,
	filter,
	find,
	keys,
	map,
	path,
	pathOr,
	pick,
	pipe,
	prop,
	propEq,
	reject, invertObj, assoc, pluck, mergeAll, isNil, reduce, concat, propOr,
} from 'ramda';
import { verifyJobIsCompleted } from '@/modules/job-profile/helpers/helpers';

export const commandMap = {
	highCommand: 'HIGH',
	mediumCommand: 'MIDDLE',
	lowCommand: 'UNDER',
};

export const commandMapInverted = invertObj(commandMap);
const concatAll = reduce(concat, []);
const getCurrentToolCode = pathOr([], ['mainData', 'code']);
const getDeleteJobIds = pipe(
	pathOr([], ['supplies', 'guidelineModel', 'userForm', 'deletedJobs']),
	map(prop('id')),
);
const getCommandByTypeVerb = job => prop(prop('typeVerb', job), commandMapInverted);
const matchWithLocalVerbs = (state, job) => verbCode => find(
	propEq('code', verbCode),
	pipe(
		pathOr([], ['supplies', 'guidelineModel', 'verbsSelection', getCommandByTypeVerb(job), 'details']),
		map(pick(['id', 'name', 'code'])),
	)(state),
);
const parseVerbs = state => job => pipe(
	propOr({}, getCommandByTypeVerb(job)),
	reject(equals(false)),
	keys,
	map(matchWithLocalVerbs(state, job)),
	reject(isNil),
)(job);


const overrideVerbStructure = (usedCommand, originalJob) => parsedJob => assoc(
	usedCommand,
	pipe(
		prop('verbsCode'),
		pluck('code'),
		map(k => ({ [k]: true })),
		mergeAll,
	)(originalJob),
	parsedJob,
);

export const parseJobs = (originalJob) => {
	const usedCommand = prop(prop('typeVerb', originalJob), commandMapInverted);
	return pipe(
		applySpec({
			additionalInformation: prop('additionalInformation'),
			id: prop('id'),
			name: prop('name'),
			role: prop('roleId'),
			title: prop('titulationId'),
			typeVerb: prop('typeVerb'),
			experience: prop('expirienceTimeId'),
			fatherId: prop('fatherId'),
			areaId: prop('areaId'),
			fatherName: prop('fatherName'),
			quantityPerson: prop('quantityPerson'),
			englishLevel: prop('englishLevelId'),
			status: prop('status'),
			isAreaManager: job => pathOr(false, ['additionalInformation', 'isAreaManager'], job),
			isHighestAuthority: job => pathOr(false, ['additionalInformation', 'isHighestAuthority'], job),
			mirrorFatherIds: job => pathOr([], ['mirrorFatherIds'], job),
			highCommand: () => ({}),
			mediumCommand: () => ({}),
			lowCommand: () => ({}),
			verbsCode: prop('verbsCode'),
		}),
		overrideVerbStructure(usedCommand, originalJob),
		job => assoc('completed', verifyJobIsCompleted(job), job),
	)(originalJob);
};

export const parseHierarchyAssignation = assignations => map(
	applySpec({
		id: prop('id'),
		job: path(['question', 'name']),
		hasError: prop('flagFix'),
		jobPositions: pipe(
			prop('details'),
			map(parseJobs),
		),
	}),
	assignations || [],
);

const toolCommonConfig = {
	name: 'Valoración de puestos',
	languages: {
		SPA: { name: 'Nombre de la encuesta 001' },
		ENG: { name: 'Poll name 001' },
		FRA: { name: 'Nom du sondage 001' },
	},
	stageStatus: 1,
	stageDetailStatus: 1,
};

const specParseJobToRequest = (state) => {
	const validIds = new Set(
		pathOr([], ['supplies', 'guidelineModel', 'userForm', 'jobList'], state)
			.map(j => j.id),
	);
	return {
		additionalInformation: (job) => {
			const existing = prop('additionalInformation', job) || {};
			const filtered = { ...existing };
			if (Array.isArray(filtered.jobHeReplaces)) {
				filtered.jobHeReplaces = filtered.jobHeReplaces
					.filter((r) => {
						const id = r && typeof r === 'object' ? r.id : r;
						return id && validIds.has(id);
					});
			}
			if (Array.isArray(filtered.jobItReplaces)) {
				filtered.jobItReplaces = filtered.jobItReplaces
					.filter((r) => {
						const id = r && typeof r === 'object' ? r.id : r;
						return id && validIds.has(id);
					});
			}
			if (filtered.immediateBoss
				&& filtered.immediateBoss.id
				&& !validIds.has(filtered.immediateBoss.id)) {
				filtered.immediateBoss = {};
			}
			return {
				...filtered,
				isAreaManager: job.isAreaManager || false,
				isHighestAuthority: job.isHighestAuthority || false,
			};
		},
		father: prop('fatherName'),
		id: prop('id'),
		name: prop('name'),
		roleId: prop('role'),
		titulationId: prop('title'),
		expirienceTimeId: prop('experience'),
		areaId: prop('areaId'),
		quantityPerson: prop('quantityPerson'),
		englishLevelId: prop('englishLevel'),
		typeVerb: prop('typeVerb'),
		status: prop('status'),
		mirrorFatherIds: job => pathOr([], ['mirrorFatherIds'], job),
		verbsCode: (job) => {
			const parsed = parseVerbs(state)(job);
			return parsed.length > 0 ? parsed : propOr([], 'verbsCode', job);
		},
	};
};

export const parseAssignationRequest = (state, { onlyCompleted = false } = {}) => {
	// Build a father-name lookup from `hierarchyAssignation`, the same source
	// `parseOrganizationChart` reads. The stage-2 tree-walk in
	// `getGuidelineModelStatus` corrects stale fathers there, so this is the
	// reliable source. Falls back to the job's own `fatherName` when the job is
	// not present in the assignation (e.g. evaluation-360 callers).
	const fatherNameById = {};
	pathOr([], ['supplies', 'guidelineModel', 'hierarchyAssignation'], state)
		.forEach((level) => {
			(level.jobPositions || []).forEach((jp) => {
				if (jp.id) {
					fatherNameById[jp.id] = jp.fatherName || null;
				}
			});
		});
	return {
		toolCode: getCurrentToolCode(state),
		...toolCommonConfig,
		deleteIds: getDeleteJobIds(state).length > 0 ? getDeleteJobIds(state) : [],
		jobs: pipe(
			pathOr([], ['supplies', 'guidelineModel', 'userForm', 'jobList']),
			onlyCompleted ? filter(propEq('completed', true)) : jobs => jobs,
			map(applySpec({
				...specParseJobToRequest(state),
				father: job => (job.id in fatherNameById
					? fatherNameById[job.id]
					: prop('fatherName', job)) || null,
			})),
		)(state),
	};
};

export const parseOrganizationChart = state => ({
	toolCode: getCurrentToolCode(state),
	...toolCommonConfig,
	stageStatus: 1,
	stageDetailStatus: 2,
	jobs: pipe(
		pathOr([], ['supplies', 'guidelineModel', 'hierarchyAssignation']),
		pluck('jobPositions'),
		concatAll,
		map(applySpec(specParseJobToRequest(state))),
	)(state),
});

export default {};



// WEBPACK FOOTER //
// ./src/modules/job-profile/utils/helpers.js